import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DAY } from '../../components/DaysInputControl/constants';
import { IQuestionEntity } from '../../utils/form';

export const INITIAL_LIABILITIES_NAMES = {
  CURRENT_LIABILITIES: 'currentLiabilities',
  NON_CURRENT_LIABILITIES: 'nonCurrentLiabilities',
} as const;

export const CURRENT_LIABILITIES_NAMES = {
  TOTAL_ACCOUNTS_PAYABLE: 'totalAccountsPayable',
  PAYMENT_DURATION_CREDIT_PURCHASES: 'paymentDurationCreditPurchases',
  PERCENT_CREDIT_PURCHASES: 'percentCreditPurchases',
  SHORT_TERM_DEBT: 'shortTermDebt',
  CURRENT_PORTION_LONG_TERM_DEBT: 'currentPortionLongTermDebt',
  ACCRUED_LIABILITIES: 'accruedLiabilities',
  TOTAL_DEFERRED_REVENUE: 'totalDeferredRevenue',
  DEFERRED_TAX_LIABILITIES: 'deferredTaxLiabilities',
  PROVISIONS: 'provisions',
  OTHER_CURRENT_LIABILITIES: 'otherCurrentLiabilities',
} as const;

export const NON_CURRENT_LIABILITIES_NAMES = {
  TOTAL_LONG_TERM_DEBT: 'totalLongTermDebt',
  DEFERRED_TAX_LIABILITIES: 'deferredTaxLiabilities',
  TOTAL_PENSION_LIABILITIES: 'totalPensionLiabilities',
  PROVISIONS_NON_CURRENT: 'provisionsNonCurrent',
  OTHER_NON_CURRENT_LIABILITIES: 'otherNonCurrentLiabilities',
} as const;

export const omitFieldsForTotalLiabilitiesSum = [
  CURRENT_LIABILITIES_NAMES.PAYMENT_DURATION_CREDIT_PURCHASES,
  CURRENT_LIABILITIES_NAMES.PERCENT_CREDIT_PURCHASES,
];

export const useGetLiabilitiesQuestionList = () => {
  const { t, i18n } = useTranslation();
  return useMemo(() => {
    const currentLiabilitiesQuestionList: IQuestionEntity[] = [
      {
        question: t(
          'financialPlan.openingBalanceSheets.initialLiabilities.currentLiabilities.currentLiabilitiesQuestions.totalAccountsPayable',
        ),
        name: CURRENT_LIABILITIES_NAMES.TOTAL_ACCOUNTS_PAYABLE,
        adornmentType: 'currency',
      },
      {
        question: t(
          'financialPlan.openingBalanceSheets.initialLiabilities.currentLiabilities.currentLiabilitiesQuestions.paymentDurationCreditPurchases',
        ),
        name: CURRENT_LIABILITIES_NAMES.PAYMENT_DURATION_CREDIT_PURCHASES,
        inputType: DAY,
      },
      {
        question: t(
          'financialPlan.openingBalanceSheets.initialLiabilities.currentLiabilities.currentLiabilitiesQuestions.percentCreditPurchases',
        ),
        name: CURRENT_LIABILITIES_NAMES.PERCENT_CREDIT_PURCHASES,
        adornmentType: 'percentage',
      },
      {
        question: t(
          'financialPlan.openingBalanceSheets.initialLiabilities.currentLiabilities.currentLiabilitiesQuestions.shortTermDebt',
        ),
        name: CURRENT_LIABILITIES_NAMES.SHORT_TERM_DEBT,
        adornmentType: 'currency',
      },
      {
        question: t(
          'financialPlan.openingBalanceSheets.initialLiabilities.currentLiabilities.currentLiabilitiesQuestions.currentPortionLongTermDebt',
        ),
        name: CURRENT_LIABILITIES_NAMES.CURRENT_PORTION_LONG_TERM_DEBT,
        adornmentType: 'currency',
      },
      {
        question: t(
          'financialPlan.openingBalanceSheets.initialLiabilities.currentLiabilities.currentLiabilitiesQuestions.accruedLiabilities',
        ),
        name: CURRENT_LIABILITIES_NAMES.ACCRUED_LIABILITIES,
        adornmentType: 'currency',
      },
      {
        question: t(
          'financialPlan.openingBalanceSheets.initialLiabilities.currentLiabilities.currentLiabilitiesQuestions.totalDeferredRevenue',
        ),
        name: CURRENT_LIABILITIES_NAMES.TOTAL_DEFERRED_REVENUE,
        adornmentType: 'currency',
      },
      {
        question: t(
          'financialPlan.openingBalanceSheets.initialLiabilities.currentLiabilities.currentLiabilitiesQuestions.deferredTaxLiabilities',
        ),
        name: CURRENT_LIABILITIES_NAMES.DEFERRED_TAX_LIABILITIES,
        adornmentType: 'currency',
      },
      {
        question: t(
          'financialPlan.openingBalanceSheets.initialLiabilities.currentLiabilities.currentLiabilitiesQuestions.provisions',
        ),
        name: CURRENT_LIABILITIES_NAMES.PROVISIONS,
        adornmentType: 'currency',
      },
      {
        question: t(
          'financialPlan.openingBalanceSheets.initialLiabilities.currentLiabilities.currentLiabilitiesQuestions.otherCurrentLiabilities',
        ),
        name: CURRENT_LIABILITIES_NAMES.OTHER_CURRENT_LIABILITIES,
        adornmentType: 'currency',
      },
    ];

    const nonCurrentLiabilitiesQuestionList: IQuestionEntity[] = [
      {
        question: t(
          'financialPlan.openingBalanceSheets.initialLiabilities.nonCurrentLiabilities.nonCurrentLiabilitiesQuestions.totalLongTermDebt',
        ),
        name: NON_CURRENT_LIABILITIES_NAMES.TOTAL_LONG_TERM_DEBT,
        adornmentType: 'currency',
      },
      {
        question: t(
          'financialPlan.openingBalanceSheets.initialLiabilities.nonCurrentLiabilities.nonCurrentLiabilitiesQuestions.deferredTaxLiabilities',
        ),
        name: NON_CURRENT_LIABILITIES_NAMES.DEFERRED_TAX_LIABILITIES,
        adornmentType: 'currency',
      },
      {
        question: t(
          'financialPlan.openingBalanceSheets.initialLiabilities.nonCurrentLiabilities.nonCurrentLiabilitiesQuestions.totalPensionLiabilities',
        ),
        name: NON_CURRENT_LIABILITIES_NAMES.TOTAL_PENSION_LIABILITIES,
        adornmentType: 'currency',
      },
      {
        question: t(
          'financialPlan.openingBalanceSheets.initialLiabilities.nonCurrentLiabilities.nonCurrentLiabilitiesQuestions.provisionsNonCurrent',
        ),
        name: NON_CURRENT_LIABILITIES_NAMES.PROVISIONS_NON_CURRENT,
        adornmentType: 'currency',
      },
      {
        question: t(
          'financialPlan.openingBalanceSheets.initialLiabilities.nonCurrentLiabilities.nonCurrentLiabilitiesQuestions.otherNonCurrentLiabilities',
        ),
        name: NON_CURRENT_LIABILITIES_NAMES.OTHER_NON_CURRENT_LIABILITIES,
        adornmentType: 'currency',
      },
    ];
    return { currentLiabilitiesQuestionList, nonCurrentLiabilitiesQuestionList };
  }, [t, i18n]);
};

export interface IInitialLiabilitiesValuesType {
  [INITIAL_LIABILITIES_NAMES.CURRENT_LIABILITIES]: {
    [CURRENT_LIABILITIES_NAMES.TOTAL_ACCOUNTS_PAYABLE]: string;
    [CURRENT_LIABILITIES_NAMES.PAYMENT_DURATION_CREDIT_PURCHASES]: string;
    [CURRENT_LIABILITIES_NAMES.PERCENT_CREDIT_PURCHASES]: string;
    [CURRENT_LIABILITIES_NAMES.SHORT_TERM_DEBT]: string;
    [CURRENT_LIABILITIES_NAMES.CURRENT_PORTION_LONG_TERM_DEBT]: string;
    [CURRENT_LIABILITIES_NAMES.ACCRUED_LIABILITIES]: string;
    [CURRENT_LIABILITIES_NAMES.TOTAL_DEFERRED_REVENUE]: string;
    [CURRENT_LIABILITIES_NAMES.DEFERRED_TAX_LIABILITIES]: string;
    [CURRENT_LIABILITIES_NAMES.PROVISIONS]: string;
    [CURRENT_LIABILITIES_NAMES.OTHER_CURRENT_LIABILITIES]: string;
  };
  [INITIAL_LIABILITIES_NAMES.NON_CURRENT_LIABILITIES]: {
    [NON_CURRENT_LIABILITIES_NAMES.TOTAL_LONG_TERM_DEBT]: string;
    [NON_CURRENT_LIABILITIES_NAMES.DEFERRED_TAX_LIABILITIES]: string;
    [NON_CURRENT_LIABILITIES_NAMES.TOTAL_PENSION_LIABILITIES]: string;
    [NON_CURRENT_LIABILITIES_NAMES.PROVISIONS_NON_CURRENT]: string;
    [NON_CURRENT_LIABILITIES_NAMES.OTHER_NON_CURRENT_LIABILITIES]: string;
  };
}

export const defaultCurrentLiabilitiesFieldValue = {
  [CURRENT_LIABILITIES_NAMES.TOTAL_ACCOUNTS_PAYABLE]: '',
  [CURRENT_LIABILITIES_NAMES.PAYMENT_DURATION_CREDIT_PURCHASES]: '30 days',
  [CURRENT_LIABILITIES_NAMES.PERCENT_CREDIT_PURCHASES]: '',
  [CURRENT_LIABILITIES_NAMES.SHORT_TERM_DEBT]: '',
  [CURRENT_LIABILITIES_NAMES.CURRENT_PORTION_LONG_TERM_DEBT]: '',
  [CURRENT_LIABILITIES_NAMES.ACCRUED_LIABILITIES]: '',
  [CURRENT_LIABILITIES_NAMES.TOTAL_DEFERRED_REVENUE]: '',
  [CURRENT_LIABILITIES_NAMES.DEFERRED_TAX_LIABILITIES]: '',
  [CURRENT_LIABILITIES_NAMES.PROVISIONS]: '',
  [CURRENT_LIABILITIES_NAMES.OTHER_CURRENT_LIABILITIES]: '',
};

export const defaultNonCurrentLiabilitiesFieldValue = {
  [NON_CURRENT_LIABILITIES_NAMES.TOTAL_LONG_TERM_DEBT]: '',
  [NON_CURRENT_LIABILITIES_NAMES.DEFERRED_TAX_LIABILITIES]: '',
  [NON_CURRENT_LIABILITIES_NAMES.TOTAL_PENSION_LIABILITIES]: '',
  [NON_CURRENT_LIABILITIES_NAMES.PROVISIONS_NON_CURRENT]: '',
  [NON_CURRENT_LIABILITIES_NAMES.OTHER_NON_CURRENT_LIABILITIES]: '',
};

export const initialLiabilitiesInitialValues = {
  [INITIAL_LIABILITIES_NAMES.CURRENT_LIABILITIES]: defaultCurrentLiabilitiesFieldValue,
  [INITIAL_LIABILITIES_NAMES.NON_CURRENT_LIABILITIES]: defaultNonCurrentLiabilitiesFieldValue,
};
